export const WIZARD_PATH = '/ai/agents/setup'

// Stands in for the store name in copy and starter messages when the merchant has no store yet.
export const FALLBACK_STORE_NAME = 'your store'

export const WIZARD_STEPS = [
  { key: 'voice', label: 'Your voice' },
  { key: 'about', label: 'About your business' },
  { key: 'winback', label: 'Win back' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'carts', label: 'Abandoned carts' },
  { key: 'upsell', label: 'Add to orders' },
  { key: 'review', label: 'Review & test' }
]

export const AGENT_STEP_KEYS = ['winback', 'reviews', 'carts', 'upsell']
export const STORE_STEP_KEYS = ['carts', 'upsell']

export const STEP_AGENT_TYPES = {
  winback: 'WinbackAgent',
  reviews: 'ReviewAgent',
  carts: 'AbandonCartAgent',
  upsell: 'UpsellAgent'
}

export const visibleSteps = () => WIZARD_STEPS.map((step, index) => ({ ...step, index }))
export const stepForKey = key => WIZARD_STEPS.find(step => step.key === key)

export const CART_DELAYS = [
  { key: '30m', label: '30 minutes', value: 30, type: 'minutes' },
  { key: '1h', label: '1 hour', value: 1, type: 'hours' },
  { key: '3h', label: '3 hours', value: 3, type: 'hours' },
  { key: '1d', label: 'Next day', value: 24, type: 'hours' }
]

export const UPSELL_LEADS = [
  { key: '2h', label: '2 hours', value: 2, type: 'hours' },
  { key: '5h', label: '5 hours', value: 5, type: 'hours' },
  { key: '1d', label: '1 day', value: 24, type: 'hours' }
]

export const defaultWizardData = () => ({
  voice: {
    tone: 'warm',
    formality_level: 'casual',
    enthusiasm_level: 'high',
    emoji_enabled: false,
    brand_voice_summary: ''
  },
  about: { additional_context: '' },
  winback: {
    enabled: true,
    lost_window_days: 90,
    slipping_enabled: true,
    lost_message: '',
    slipping_message: '',
    offer_enabled: false,
    discount_code_id: ''
  },
  reviews: { enabled: true, order_threshold: 3, google_review_link: '', checkin_message: '' },
  carts: {
    enabled: true,
    delay: '1h',
    message: '',
    offer_enabled: false,
    discount_code_id: '',
    store_ids: [],
    per_store: false,
    stores: {}
  },
  upsell: { enabled: true, lead: '5h', message: '', store_ids: [], per_store: false, stores: {} },
  starters_applied: false
})

// starter messages

export const starterMessages = store => ({
  winback_lost: `Hey {{{first_name}}}, it's ${store} here. We've missed you! Our menu has changed since you last ordered and I'd love to get you back on it. Want me to send over what's on this week?`,
  winback_slipping: `Hey {{{first_name}}}, it's been a little while since your last order with ${store}. Anything we can do to make ordering easier this week? Happy to send the menu over.`,
  reviews: `Hi {{{first_name}}}, ${store} here. You've ordered with us a few times now and we really appreciate it. How have the meals been?`,
  carts: `Hey {{{first_name}}}, ${store} here. Looks like you left some meals in your cart. Want a hand finishing up before ordering closes?`,
  upsell: `Hey {{{first_name}}}, ${store} here! Your order is locked in. Ordering closes soon, want to add anything before then? A few favourites are still available.`
})

const fillBlank = (section, field, text) => {
  if (!section[field].trim()) section[field] = text
}

export const applyStarters = (data, storeName) => {
  if (data.starters_applied) return data
  const starters = starterMessages(storeName)
  fillBlank(data.winback, 'lost_message', starters.winback_lost)
  fillBlank(data.winback, 'slipping_message', starters.winback_slipping)
  fillBlank(data.reviews, 'checkin_message', starters.reviews)
  fillBlank(data.carts, 'message', starters.carts)
  fillBlank(data.upsell, 'message', starters.upsell)
  data.starters_applied = true
  return data
}

// copy

const HEADINGS = {
  voice: () => 'How should your agents sound?',
  about: ({ storeName }) => `What should your agents know about ${storeName}?`,
  winback: () => 'Bring back customers who have drifted away',
  reviews: () => 'Turn happy regulars into Google reviews',
  carts: ({ storeName, subStep }) =>
    subStep ? `Abandoned carts for ${storeName}` : 'Rescue carts that got left behind',
  upsell: ({ storeName, subStep }) =>
    subStep ? `Add to orders for ${storeName}` : 'Add to orders before the kitchen closes',
  review: ({ created }) => (created ? 'Your agents are ready. Try them on yourself first.' : "Here's what we'll set up")
}

export const stepHeading = (key, data, ctx = {}) => (HEADINGS[key] ? HEADINGS[key](ctx) : '')

const SUBTITLES = {
  voice: () => 'Every agent texts in this voice. You set it once; change it any time under General settings.',
  about: () => 'Agents answer real questions. The more they know, the fewer they hand to you.',
  winback: () =>
    "Most meal-prep customers don't cancel. They just stop ordering. Winback notices when a regular has gone quiet, sends them one friendly text, and if they reply it answers like a member of your team and points them back to your menu. It runs once a day and never texts the same person twice in a month.",
  reviews: () =>
    "After a customer's third order, Reviews checks in by text and asks how the meals have been. Happy customers get your Google review link. Unhappy ones get heard, and their feedback comes to you instead of the internet.",
  carts: () =>
    'People fill a cart on their phone, get distracted, and never check out. Abandoned carts waits a little, then sends one text offering to help them finish. If they reply, it answers questions and sends them straight back to their cart. It only texts between 9am and 7pm.',
  upsell: () =>
    'A few hours before ordering closes, Add to orders texts customers who already have a paid order and offers your add-ons. If they say yes, it adds the items and charges the card on file. It is the agent most likely to pay for itself in week one.',
  review: ({ created, testingCount = 0 }) => {
    if (!created || testingCount === 0) return ''
    const count = testingCount === 1 ? '1 agent is' : `${testingCount} agents are`
    return `${count} in testing: they will talk to you, and to nobody else, until you switch each one live.`
  }
}

const SUB_STEP_SUBTITLE = ({ storeName }) =>
  `These settings apply to ${storeName} only. Your other stores keep the shared settings.`

export const stepSubtitle = (key, data, ctx = {}) => {
  if (!SUBTITLES[key]) return ''
  return ctx.subStep && STORE_STEP_KEYS.includes(key) ? SUB_STEP_SUBTITLE(ctx) : SUBTITLES[key](ctx)
}

export const ABOUT_PROMPTS = [
  'Pickup days and times',
  'Delivery areas',
  'Dietary options you cater for',
  'How far ahead to order',
  'How to reach a human',
  'Anything the agent must never promise'
]
export const ABOUT_NOTE = 'Agents already know your menu, prices and ordering windows. Add what is not on your store.'

const lowerFirst = text => text.charAt(0).toLowerCase() + text.slice(1)
const aboutDescription = () =>
  `${ABOUT_PROMPTS.map((prompt, i) => (i === 0 ? prompt : lowerFirst(prompt))).join(', ')}.`

const section =
  (title, description = '') =>
  () => ({ title, description })

const SECTIONS = {
  'voice.tone': section('Tone', 'Pick the personality your customers will hear.'),
  'voice.formality': section('Formality', 'How buttoned-up the wording should be.'),
  'voice.enthusiasm': section('Enthusiasm', 'How much energy comes through in each text.'),
  'voice.emoji': section('Emoji', 'A little goes a long way with older customers.'),
  'voice.brand': section(
    'Describe your brand in a sentence or two',
    "How would a regular describe texting with you? For example: 'Like a friend who happens to run a kitchen. Short, warm, never salesy.'"
  ),
  'about.context': section('Things customers ask', aboutDescription()),
  'agent.choose': section('Should we set this one up?'),
  'winback.quiet': section('Who counts as gone quiet?'),
  'winback.message': section(
    'What should the first text say?',
    'This is the only text written by you. Replies are handled by the agent in your voice. Use {{{first_name}}} for their name.'
  ),
  'winback.offer': section('Offer a discount?'),
  'reviews.when': section('When to ask'),
  'reviews.link': section(
    'Your Google review link',
    "In Google Business Profile, choose 'Ask for reviews' and copy the link."
  ),
  'reviews.message': section('The check-in text'),
  'carts.wait': section('How long to wait'),
  'carts.message': section('The first text'),
  'carts.offer': section('Offer a discount?'),
  'carts.stores': section('Which stores', 'Same message and timing for every ticked store.'),
  'upsell.addons': section('Which add-ons'),
  'upsell.lead': section('How long before cutoff'),
  'upsell.message': section('The first text'),
  'upsell.stores': section('Which stores', 'Same message and timing for every ticked store.')
}

export const sectionCopy = (sectionKey, data, ctx = {}) =>
  SECTIONS[sectionKey] ? SECTIONS[sectionKey](ctx) : { title: '', description: '' }

// per-store settings

const STORE_FIELDS = {
  carts: ['delay', 'message', 'offer_enabled', 'discount_code_id'],
  upsell: ['lead', 'message']
}

export const storeSettings = (data, stepKey, storeId) => {
  const shared = data[stepKey]
  const source = shared.per_store ? { ...shared, ...shared.stores[String(storeId)] } : shared
  return Object.fromEntries(STORE_FIELDS[stepKey].map(field => [field, source[field]]))
}

// validity

const filled = value => Boolean(value && value.trim())

const storeStepValid = (stepKey, data) => {
  const step = data[stepKey]
  if (step.store_ids.length === 0) return false
  if (!step.per_store) return filled(step.message)
  return step.store_ids.every(id => filled(step.stores[String(id)]?.message))
}

const winbackValid = ({ winback }) =>
  filled(winback.lost_message) && (!winback.slipping_enabled || filled(winback.slipping_message))

const reviewsValid = ({ reviews }) => filled(reviews.google_review_link) && filled(reviews.checkin_message)

const AGENT_VALIDATORS = {
  winback: winbackValid,
  reviews: reviewsValid,
  carts: data => storeStepValid('carts', data),
  upsell: data => storeStepValid('upsell', data)
}

export const stepValid = (key, data) => {
  const validator = AGENT_VALIDATORS[key]
  if (!validator) return true
  return !data[key].enabled || validator(data)
}

// True when the merchant already has this agent: any one for a global agent, one per store for the store agents.
export const stepCoveredByExisting = (stepKey, existing, stores) => {
  if (!STORE_STEP_KEYS.includes(stepKey)) return existing[stepKey] === true
  return stores.length > 0 && stores.every(store => existing[stepKey].has(String(store.id)))
}

export const anyAgentEnabled = data => AGENT_STEP_KEYS.some(key => data[key].enabled)
export const allStepsValid = data => WIZARD_STEPS.every(step => stepValid(step.key, data))
